import React from "react";
import { useOrganization } from "../../../App";
import { Card, CardContent } from "@/components/ui/card";
import { Lock } from "lucide-react";

interface FeatureGuardProps {
  featureId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * A component that conditionally renders its children based on whether
 * a specific feature is enabled for the current organization.
 */
export function FeatureGuard({
  featureId,
  children,
  fallback,
}: FeatureGuardProps) {
  const { currentOrganization } = useOrganization();

  // Check if the feature is enabled for this organization
  // Default to enabled if feature_flags is undefined or the specific feature is not defined
  const isFeatureEnabled =
    currentOrganization?.feature_flags === undefined ||
    currentOrganization?.feature_flags?.[featureId] !== false;

  if (isFeatureEnabled) {
    return <>{children}</>;
  }

  // If feature is disabled, show fallback or default message
  return (
    fallback || (
      <Card className="w-full border shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Lock className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Feature Not Available</h3>
          <p className="text-muted-foreground text-center max-w-md">
            The {featureId} feature is not enabled for your organization. Please
            contact your administrator for access.
          </p>
        </CardContent>
      </Card>
    )
  );
}
